import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

interface MarkPaidDialogProps {
  open: boolean;
  invoiceId: string | null;
  invoiceNumber?: string;
  onConfirm: (invoiceId: string, paymentMethod: string, paymentNotes: string) => void;
  onCancel: () => void;
}

export function MarkPaidDialog({
  open,
  invoiceId,
  invoiceNumber,
  onConfirm,
  onCancel,
}: Readonly<MarkPaidDialogProps>) {
  const { t } = useTranslation();
  const [paymentMethod, setPaymentMethod] = useState('');
  const [paymentNotes, setPaymentNotes] = useState('');

  useEffect(() => {
    if (open) {
      setPaymentMethod('');
      setPaymentNotes('');
    }
  }, [open]);

  if (!open || !invoiceId) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      data-testid="mark-paid-dialog"
    >
      <Card className="w-full max-w-md mx-4">
        <CardHeader>
          <CardTitle>
            {t('invoice.paymentDetails')}
            {invoiceNumber && (
              <span className="ml-2 text-sm font-normal text-muted-foreground">
                {invoiceNumber}
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label className="text-sm font-medium" htmlFor="payment-method">
              {t('invoice.paymentMethod')}
            </label>
            <Input
              id="payment-method"
              value={paymentMethod}
              onChange={(e) => setPaymentMethod(e.target.value)}
              placeholder={t('invoice.paymentMethod')}
            />
          </div>
          <div>
            <label className="text-sm font-medium" htmlFor="payment-notes">
              {t('invoice.paymentNotes')}
            </label>
            <textarea
              id="payment-notes"
              className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={paymentNotes}
              onChange={(e) => setPaymentNotes(e.target.value)}
              placeholder={t('invoice.paymentNotes')}
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={onCancel}>
              {t('invoice.cancel')}
            </Button>
            <Button onClick={() => onConfirm(invoiceId, paymentMethod, paymentNotes)}>
              {t('invoice.confirmPayment')}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
